import {Injectable, Renderer2, RendererFactory2} from '@angular/core';

/**
 * Angular-safe DOM manipulation utilities
 * Uses Renderer2 for all DOM operations to ensure compatibility with Angular's change detection
 */
@Injectable({
  providedIn: 'root',
})
export class DomUtilsService {
  private renderer: Renderer2;

  constructor(private rendererFactory: RendererFactory2) {
    // Create a renderer instance that works outside component context
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Safely decode HTML entities without jQuery
   */
  decodeHtmlEntities(html: string): string {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  /**
   * Smooth scroll animation for horizontal scrolling
   * Uses native scrollTo with fallback for older browsers
   */
  smoothHorizontalScroll(
    element: HTMLElement,
    targetScrollLeft: number,
    duration: number = 300,
  ): Promise<void> {
    return new Promise(resolve => {
      // Try native smooth scrolling first
      if ('scrollBehavior' in document.documentElement.style) {
        element.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
        // Estimate completion time
        setTimeout(resolve, duration);
      } else {
        // Fallback animation using requestAnimationFrame
        const startScrollLeft = element.scrollLeft;
        const distance = targetScrollLeft - startScrollLeft;
        const startTime = performance.now();

        const animateScroll = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function (ease-in-out)
          const easeProgress =
            progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          element.scrollLeft = startScrollLeft + distance * easeProgress;

          if (progress < 1) {
            requestAnimationFrame(animateScroll);
          } else {
            resolve();
          }
        };

        requestAnimationFrame(animateScroll);
      }
    });
  }

  /**
   * Find all elements matching a selector within a parent
   * Returns empty array if no matches found
   */
  findElements(parent: HTMLElement, selector: string): HTMLElement[] {
    if (!parent) return [];
    return Array.from(parent.querySelectorAll<HTMLElement>(selector));
  }

  /**
   * Get closest ancestor matching selector
   */
  closest(element: HTMLElement, selector: string): HTMLElement | null {
    if (!element) return null;
    return element.closest<HTMLElement>(selector);
  }

  /**
   * Calculate total parent depth for hierarchy operations
   */
  getParentDepth(element: HTMLElement, rootSelector?: string): number {
    let depth = 0;
    let current = element.parentElement;

    while (current) {
      if (rootSelector && current.matches(rootSelector)) {
        break;
      }
      depth++;
      current = current.parentElement;
    }

    return depth;
  }

  /**
   * Check if element has children matching selector
   */
  hasChildren(element: HTMLElement, selector: string): boolean {
    return element.querySelector(selector) !== null;
  }

  /**
   * Get direct children matching selector
   */
  getDirectChildren(element: HTMLElement, selector: string): HTMLElement[] {
    if (!element) return [];
    const allChildren = Array.from(element.children) as HTMLElement[];
    return allChildren.filter(child => child.matches(selector));
  }
}
